import { useState } from 'react';
import intl from 'react-intl-universal';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { TabPanels, TabPanel, Tabs } from '@reach/tabs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Tab, TabList } from '@getsynapse/design-system';
import { PATHS, SETTINGS_TABS } from 'utils/constants';
import {
  selectProjectProcessIdToUpdate,
  setProcessIdToUpdate,
} from 'state/Processes/processesSlice';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import AccountSettings from './AccountSettings/AccountSettings';
import PlatformSettings from './Configurations/Configurations';
import UsersListPage from './UsersListPage/UsersListPage';
import Teams from './TeamsSettings/TeamsSettings';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSIONS } from 'utils/permissions';

const SettingsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { budgetManagement = false } = useFlags();
  const [canAccessUsersSettings] = usePermissions([
    PERMISSIONS.ACCESS_USERS_SETTINGS,
  ]);

  const showAccount = !budgetManagement;
  const showUsers = canAccessUsersSettings;

  const accountOffset = showAccount ? 1 : 0;
  const usersOffset = showUsers ? 1 : 0;
  const teamsIdx = accountOffset + usersOffset;
  const configsIdx = teamsIdx + 1;

  const { activeTab } = useParams<{ activeTab: string }>();
  const tabMapping: Record<string, number> = {
    ...(showUsers && { [SETTINGS_TABS.USERS]: accountOffset }),
    [SETTINGS_TABS.TEAMS]: teamsIdx,
    [SETTINGS_TABS.CONFIGURATIONS]: configsIdx,
  };
  const [activeIndex, setActiveIndex] = useState(
    activeTab && tabMapping[activeTab] !== undefined
      ? tabMapping[activeTab]
      : teamsIdx
  );
  const isUpdatingProjectProcess =
    useSelector(selectProjectProcessIdToUpdate) !== null;

  const handleChangeTab = (tab: number) => {
    setActiveIndex(tab);
    const tabKey = Object.entries(tabMapping).find(
      ([, idx]) => idx === tab
    )?.[0];
    if (tabKey) {
      history.push(`${PATHS.SETTINGS}/${tabKey}`);
    }
    if (isUpdatingProjectProcess) {
      dispatch(setProcessIdToUpdate(null));
    }
  };

  return (
    <Tabs
      className='flex flex-col min-h-full max-w-full'
      index={activeIndex || 0}
      onChange={handleChangeTab}
    >
      <PageTitle
        titleComponent={intl.get('SIDEBAR.SETTINGS')}
        headerChildren={
          <TabList type='subHeader'>
            {showAccount && (
              <Tab index={0} type='subHeader' data-cy='account'>
                {intl.get('ENTITIES.ACCOUNT', { num: 1 })}
              </Tab>
            )}
            {showUsers && (
              <Tab index={accountOffset} type='subHeader' data-cy='users'>
                {intl.get('ENTITIES.USER', { num: 2 })}
              </Tab>
            )}
            <Tab index={teamsIdx} type='subHeader' data-cy='teams'>
              {intl.get('ENTITIES.TEAM', { num: 2 })}
            </Tab>
            <Tab index={configsIdx} type='subHeader' data-cy='platform-tab'>
              {intl.get('ENTITIES.CONFIGURATION', { num: 2 })}
            </Tab>
          </TabList>
        }
      />

      <TabPanels className='mx-6 flex flex-grow'>
        {showAccount && (
          <TabPanel key={0} className='flex-grow'>
            <AccountSettings />
          </TabPanel>
        )}
        {showUsers && (
          <TabPanel key={accountOffset} className='flex-grow max-w-full'>
            <UsersListPage />
          </TabPanel>
        )}
        <TabPanel key={teamsIdx} className='flex-grow'>
          <Teams />
        </TabPanel>
        <TabPanel key={configsIdx} className='flex-grow max-w-full'>
          <PlatformSettings />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default SettingsPage;
